import { viewWithModel, prop } from '@vscode/observables-react';
import { ExplorerModel } from './ExplorerModel.js';
import { TitleBar } from './TitleBar.js';
import { LeftSidebar } from './LeftSidebar.js';
import { RightSidebar } from './RightSidebar.js';
import { PreviewArea } from './PreviewArea.js';
import { ConsolePanel } from './ConsolePanel.js';
import { ConsoleEventsStore } from '../core/ConsoleEventsStore.js';
import { styles } from './styles.js';
import type { DaemonModel } from '../daemon/index.js';
import type { ReportModel } from '../report/ReportModel.js';

// Install global console events capture
const consoleEventsStore = ConsoleEventsStore.getInstance();
consoleEventsStore.install();

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  height: '100%',
  width: '100%',
  color: 'var(--component-explorer-foreground)',
  opacity: 0.5,
  fontSize: '13px',
};

const spinnerStyle: React.CSSProperties = {
  width: '24px',
  height: '24px',
  border: '2px solid currentColor',
  borderTopColor: 'transparent',
  borderRadius: '50%',
  animation: 'explorer-spin 0.8s linear infinite',
};

export const Explorer = viewWithModel(
  ExplorerModel,
  { daemonModel: prop.const<DaemonModel | undefined>(), reportModel: prop.const<ReportModel | undefined>() },
  (reader, model) => {
    const isDarkTheme = model.isDarkTheme.read(reader);
    const loading = model.loading.read(reader);
    const error = model.error.read(reader);

    const themeClass = isDarkTheme ? 'vscode-theme default-dark-plus' : 'vscode-theme default-light-plus';

    if (error) {
      return (
        <div className={themeClass} style={{ ...styles.container, backgroundColor: 'var(--component-explorer-editor-background)' }}>
          <div style={loadingStyle}>
            <span style={{ color: 'var(--component-explorer-errorForeground)' }}>Failed to load fixtures</span>
            <pre style={{ fontSize: '12px', opacity: 0.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'inherit' }}>{error}</pre>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className={themeClass} style={{ ...styles.container, backgroundColor: 'var(--component-explorer-editor-background)' }}>
          <style>{`@keyframes explorer-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={loadingStyle}>
            <div style={spinnerStyle} />
            Loading fixtures…
          </div>
        </div>
      );
    }

    const leftVisible = model.leftSidebarVisible.read(reader);
    const rightVisible = model.rightSidebarVisible.read(reader);
    const selectedNode = model.selectedNode.read(reader);

    return (
      <div className={themeClass} style={styles.container}>
        {leftVisible && <LeftSidebar model={model} />}
        <div style={styles.centerArea}>
          <TitleBar model={model} />
          <PreviewArea model={model} />
          <ConsolePanel store={consoleEventsStore} />
        </div>
        {rightVisible && <RightSidebar selectedNode={selectedNode} />}
      </div>
    );
  }
);
